/* eslint-disable no-console */
/* global KJUR,CryptoJS,Vue */

import allLifecycles from '@api-consent/defs/lifecycle';
import allSps from '@api-consent/defs/sps';

// esbuild: reload watcher
// https://esbuild.github.io/api/#watch
if (window.location.hostname === 'localhost') {
  new EventSource('/esbuild').addEventListener('change', () => {
    window.location.reload();
  });
}

// lifecycles and spids are taken from api-consent!
const lifecycles = allLifecycles.data
  .map((lc) => ({ label: `${lc.id} (${lc.title})`, value: lc.id }))
  .sort((a, b) => (a.value < b.value ? -1 : 1));

const spIds = allSps.data
  .map((sp) => ({ label: `${sp.id} (${sp.title})`, value: sp.id }))
  .sort((a, b) => (a.value < b.value ? -1 : 1));

const scenarios = [{
  value: 'register',
  label: 'Registration',
  description: `This is for testing new customer orders.
    A register token will be created on-the-fly based on the data below.
    Make sure you choose a suitable lifecycle.`,
  deprecated: true,
}, {
  value: 'access',
  label: 'Piranha Selfcare',
  description: `This is for testing selfcare scenarios with Piranha.
    Please provide a real Piranha access token below.
    Make sure you choose a suitable lifecycle
    as well as an API that connects via API gateway.`,
  deprecated: true,
}, {
  value: 'fakePiranha',
  label: 'Piranha Selfcare with Fake Token',
  description: `This is for testing selfcare scenarios with Piranha.
    A fake access token will be created on-the-fly based on the data below.
    Make sure you choose a suitable lifecycle
    as well as an API that directly connects to the COMS backend w/o API gateway.`,
  deprecated: true,
}, {
  value: 'customerToken',
  label: 'Customer Token',
  description: `This is for testing selfcare scenarios with a real customer token.
    Make sure you choose a suitable lifecycle.
    You can connect with both APIs, i.e., either directly
    to the COMS backend or via the API gateway (which also needs an access token).`,
  deprecated: true,
}, {
  value: 'fakeCustomerToken',
  label: 'Customer Token with Fake Token',
  description: `This is for testing selfcare scenarios with a fake customer token.
    A fake customer token will be created on-the-fly based on the data below.
    Make sure you choose a suitable lifecycle.
    You can connect with both APIs, i.e., either directly
    to the COMS backend or via the API gateway (which also needs an access token).`,
  deprecated: true,
}, {
  value: 'raitt-real',
  label: 'Real Access Token',
  description: `You need to provide a real ForgeRock access token.
    This is simulating the integrating portal.`,
  raitt: true,
}, {
  value: 'raitt-fake',
  label: 'Fake Access Token',
  description: `A fake access token will be created on-the-fly based on the properties below.
    This is simulating the integrating portal and bypassing ForgeRock.`,
  raitt: true,
}];

const propsQuery = [
  { name: 'lifecycle', type: 'string', defaultValue: 'webAppRegistrationProcess' },
  { name: 'mode', type: 'string', defaultValue: '' },
  { name: 'scenario', type: 'string', defaultValue: scenarios[0].value },
  { name: 'accesstoken', type: 'string', defaultValue: '' },
  { name: 'customerToken', type: 'string', defaultValue: '' },
  { name: 'accesstoken4p', type: 'string', defaultValue: '' },
  { name: 'newcustomer', type: 'boolean', defaultValue: false },
  { name: 'spId', type: 'string', defaultValue: 'SP901' },
  { name: 'registerId', type: 'string', defaultValue: '0815' },
  { name: 'lcId', type: 'string', defaultValue: '4711' },
  {
    name: 'apiuri',
    type: 'string',
    defaultValue: (window.location.protocol === 'http:' && window.location.hostname.indexOf('local') >= 0)
      ? (`http://${window.location.hostname}:3000`)
      : 'https://ict.high.apigw-test.aws.de.pri.o2.com/gateway/coms-ict/v2',
  },
  { name: 'apiuri4p', type: 'string', defaultValue: '' },
  { name: 'tenant', type: 'string', defaultValue: 'de-int' },
  { name: 'occluri', type: 'string', defaultValue: 'https://dev-library.telefonica.de/1' },
  { name: 'tefOne', type: 'string', defaultValue: '' },
  { name: 'tefOneEnv', type: 'string', defaultValue: 'main' },
  { name: 'stylecontext', type: 'string', defaultValue: '' },
  { name: 'traceid', type: 'string', defaultValue: '' },
  { name: 'references', type: 'string', defaultValue: '' },
  { name: 'comment', type: 'string', defaultValue: '' },
  { name: 'usertype', type: 'string', defaultValue: '' },
];

const propsLocalStorage = [
  { name: 'clearSessionStorage', defaultValue: true },
];

const shortcuts = {
  mode: [
    { value: '', label: 'none' },
    { value: 'buttons', label: 'buttons' },
    { value: 'buttons:save', label: 'buttons:save' },
    { value: 'buttons:saveActive', label: 'buttons:saveActive' },
    { value: 'multiedit', label: 'multiedit' },
    { value: 'multiedit:0', label: 'multiedit:0' },
    { value: 'multiedit:1', label: 'multiedit:1' },
    { value: 'multiedit:erlaubnisse', label: 'multiedit:erlaubnisse' },
    { value: 'multiedit:einwilligungen', label: 'multiedit:einwilligungen' },
    { value: 'multiedit:0,buttons:cancel', label: 'multiedit:0,buttons:cancel' },
    { value: 'merge', label: 'merge' },
  ],
  apiuriGateway: [
    { value: 'https://ict.high.apigw-test.aws.de.pri.o2.com/gateway/coms-core/v2', label: 'ict' },
    { value: 'https://e2e2.high.apigw-test.aws.de.pri.o2.com/gateway/coms-core/v2', label: 'e2e2' },
    { value: 'https://e2e1.high.apigw-test.aws.de.pri.o2.com/gateway/coms-core/v2', label: 'e2e1' },
    { value: 'https://prod.high.apigw-prod.aws.de.pri.o2.com/gateway/coms-core/v2', label: 'prod' },
  ],
  apiuriDirect: [
    { value: 'http://localhost:3000', label: 'localhost' },
    { value: 'https://main-api-consent.high.coms-dev.aws.de.pri.o2.com', label: 'main' },
    { value: 'https://ict-api-consent.high.coms-dev.aws.de.pri.o2.com', label: 'ict' },
    { value: 'https://e2e2-api-consent.high.coms-dev.aws.de.pri.o2.com', label: 'e2e2' },
    { value: 'https://e2e1-api-consent.high.coms-dev.aws.de.pri.o2.com', label: 'e2e1' },
    { value: 'https://api-consent.high.coms-prod.aws.de.pri.o2.com', label: 'prod' },
  ],
  occluriDev: [
    { value: 'https://dev-library.telefonica.de/1', label: 'o2' },
    { value: 'https://dev-library.telefonica.de/2', label: 'Blau' },
    { value: 'https://dev-library.telefonica.de/65', label: 'aetkaSMART' },
    { value: 'https://dev-library.telefonica.de/72', label: 'Ayyildiz' },
    { value: 'https://dev-library.telefonica.de/75', label: 'NettoKOM' },
    { value: 'https://dev-library.telefonica.de/76', label: 'novamobil' },
    { value: 'https://dev-library.telefonica.de/78', label: 'WhatsApp SIM' },
    { value: 'https://dev-library.ortelmobile.de/77', label: 'Ortel' },
    { value: 'https://dev-library.ortelmobile.de/79', label: 'Mobilka (Ortel)' },
    { value: 'https://dev-library.ortelmobile.de/86', label: 'blauworld (Ortel)' },
    { value: 'https://dev-library.alditalk-kundenbetreuung.de/80', label: 'Aldi Talk' },
  ],
  occluriProd: [
    { value: 'https://library.telefonica.de/1', label: 'o2' },
    { value: 'https://library.telefonica.de/2', label: 'Blau' },
    { value: 'https://library.telefonica.de/65', label: 'aetkaSMART' },
    { value: 'https://library.telefonica.de/72', label: 'Ayyildiz' },
    { value: 'https://library.telefonica.de/75', label: 'NettoKOM' },
    { value: 'https://library.telefonica.de/76', label: 'novamobil' },
    { value: 'https://library.telefonica.de/78', label: 'WhatsApp SIM' },
    { value: 'https://library.ortelmobile.de/77', label: 'Ortel' },
    { value: 'https://library.ortelmobile.de/79', label: 'Mobilka (Ortel)' },
    { value: 'https://library.ortelmobile.de/86', label: 'blauworld (Ortel)' },
    { value: 'https://library.alditalk-kundenbetreuung.de/80', label: 'Aldi Talk' },
  ],
  tefOne: [
    { value: 'nettokom', label: 'NettoKOM' },
    { value: 'whatsappsim', label: 'WhatsApp SIM' },
    { value: 'blau', label: 'Blau' },
    { value: 'ayyildiz', label: 'Ayyildiz' },
    { value: 'o2', label: 'o2' },
    { value: 'ortel', label: 'Ortel' },
    { value: 'alditalk', label: 'alditalk' },
  ],
  tefOneEnv: [
    { value: 'main', label: 'main' },
    { value: 'staging', label: 'staging' },
  ],
  stylecontext: [
    { value: '', label: 'none' },
    { value: 'shop', label: 'shop' },
    { value: 'selfcare', label: 'selfcare' },
    { value: 'ios', label: 'ios' },
    { value: 'android', label: 'android' },
  ],
};

function createJwt(payload, secret = 'ach-wie-gut-dass-niemand-weiss-9') {
  console.info('token payload is', payload);
  const header = { alg: 'HS256', typ: 'JWT', kid: 'test' };
  return KJUR.jws.JWS.sign(
    header.alg,
    JSON.stringify(header),
    JSON.stringify(payload),
    secret,
  );
}

function createComs({
  scenario,
  customerToken, accesstoken, accesstoken4p,
  newcustomer, lifecycle, mode,
  spId, registerId, lcId,
  apiuri, apiuri4p, occluri, stylecontext,
  traceid, references, comment, usertype,
  clearSessionStorage,
}) {
  if (clearSessionStorage) {
    window.sessionStorage.clear();
  }
  const coms = document.createElement('tef-coms');
  const setAttributes = (attrs) => Object
    .entries(attrs)
    .filter(([, value]) => value) // ignore '', false, undefined
    .forEach(([name, value]) => coms.setAttribute(name, value));
  // set scenario-independent attributes
  setAttributes({
    id: 'consents',
    lifecycle,
    mode,
    apiuri,
    apiuri4p,
    occluri,
    stylecontext,
    traceid,
    references,
    comment,
    usertype,
  });
  // set scenario-specific attributes
  if (scenario === 'register') {
    const token = createJwt({
      spId,
      registerId,
      iss: 'register.test',
      sub: 'dat sub',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 60 * 60, // 1 hour
      aud: 'coms.telefonica.de',
    });
    setAttributes({
      registertoken: token,
      accesstoken,
    });
  } else if (scenario === 'customerToken') {
    setAttributes({
      customertoken: customerToken,
      accesstoken,
    });
  } else if (scenario === 'fakeCustomerToken') {
    const token = createJwt({
      iss: 'salesforce.telefonica.de',
      sub: 'subscriber',
      aud: 'coms.telefonica.de',
      spId,
      lcId,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 60 * 60, // 1 hour
    });
    const aesKeyBase64 = 'NHhxbmt1MzRnY24yaXV3eGdyeGtqY3dnaDRuc3V3eWo='; // must decode to 16 or 32 bytes
    const ivBase64 = '5D9r9ZVzEYYgha93/aUK2w=='; // must be 16 bytes !!
    const key = CryptoJS.enc.Base64.parse(aesKeyBase64);
    const iv = CryptoJS.enc.Base64.parse(ivBase64);
    const encrypted = CryptoJS.AES.encrypt(token, key, {
      mode: CryptoJS.mode.CBC,
      iv,
    });
    setAttributes({
      customertoken: encrypted,
      accesstoken: accesstoken || 'fakeAccesToken',
    });
  } else if (scenario === 'fakePiranha') {
    const token = createJwt({
      iss: 'http://piranha.fake.de',
      sub: 'forgerock fake token',
      aud: 'coms.telefonica.de',
      spId,
      lcId,
      username: 'mf-coms',
      scope: ['consent.read', 'consent.write', 'u-672:trusted'],
    });

    // create fakeAccessToken for api4p
    const nowSecs = Math.floor(Date.now() / 1000);
    const fakeToken4p = createJwt({
      iss: 'http://fake.issuer.for.coms.simulator.telefonica.de',
      sub: 'sub',
      aud: 'aud',
      iat: nowSecs,
      exp: nowSecs + 43200, // 1 hour
      scope: ['u-672:trusted'],
    });
    setAttributes({
      accesstoken: token,
      accesstoken4p: fakeToken4p,
      spId,
      lcId
    });
  } else if (scenario === 'access') { // Piranha
    setAttributes({
      accesstoken,
      accesstoken4p,
    });
  } else if (scenario === 'raitt-fake' || scenario === 'raitt-real') {
    setAttributes({
      spId,
      registerId,
      lcId,
      newcustomer: newcustomer ? true : undefined,
    });
    // in addition, authorization header is attached by Service Worker, see below
  }
  document.getElementById('mf').appendChild(coms);
}

const components = {
  deprecated: {
    template: /* html */`
      <span class="badge bg-danger rounded-pill">deprecated</span>
    `,
  },
  raitt: {
    template: /* html */`
      <span class="badge bg-primary rounded-pill">new raitt</span>
    `,
  },
  attribute: {
    template: /* html */`
      <span class="badge bg-warning rounded-pill">attribute</span>
    `,
  },
  workaround: {
    template: /* html */`
      <span class="badge bg-dark rounded-pill">workaround</span>
    `,
  },
};

const app = new Vue({ // eslint-disable-line no-unused-vars
  el: '#app',

  data: {
    ...[...propsQuery, ...propsLocalStorage] // add all props
      .reduce((acc, { name, defaultValue }) => ({ ...acc, [name]: defaultValue }), {}),
    error: null,
    info: null,
    ready: false,
    scopesOauth: 'openid',
    fakeOauthToken: null,
    lifecycles,
    shortcuts,
    scenarios,
    spIds,
  },

  components,

  created() {
    this.init();
  },

  methods: {
    async startServiceWorker() {
      return new Promise((resolve) => {
        // register service worker
        window.addEventListener('load', () => {
          navigator
            .serviceWorker
            .register('./request-interceptor.js')
            .then((registration) => {
              console.info('ServiceWorker registered with scope', registration.scope);
            }, (error) => {
              console.error('ServiceWorker registration failed', error);
            });
        });
        // configure service worker when ready
        navigator.serviceWorker.ready.then((registration) => {
          console.info('ServiceWorker ready');
          if (registration.active) {
            console.info('ServiceWorker active');
            const {
              scenario, accesstoken, apiuri,
              apiuri4p, tenant, lcId, spId,
            } = app;
            const userid4p = `lcId:${lcId}::spId:${spId}::username:mf-coms`;
            const jwtAssertion = createJwt({
              sub: userid4p,
              'http://wso2.org/claims/applicationname': 'applicationname',
              'http://wso2.org/claims/apiname': 'apiname',
              'http://wso2.org/claims/apicontext': 'apicontext',
            });
            if (scenario === 'raitt-real') {
              registration.active.postMessage({
                type: 'APPDATA',
                apiuri,
                accesstoken,
              });
            } else if (scenario === 'raitt-fake') {
              const token = createJwt({
                // new ForgeRock token format Jan 2023
                iss: 'https://fake.issuer.for.coms.simulator.telefonica.de',
                sub: 'fake-customer',
                username: ['491797250005', 'foo@bar.de'],
                spID: spId,
                domainModel: lcId ? [{
                  customerAccountId: lcId,
                  role: 'Owner',
                }] : [],
                scope: ['openid'], // oidc
              });
              registration.active.postMessage({
                type: 'APPDATA',
                apiuri,
                accesstoken: token,
              });
            } else if (scenario === 'access' || scenario === 'fakePiranha') {
              registration.active.postMessage({
                type: 'APPDATA',
                apiuri4p,
                jwtAssertion,
                tenant,
                userid4p,
              });
            } else { // important!
              registration.active.postMessage({
                type: 'RESET',
              });
            }
            resolve();
          }
        });
      });
    },

    async loadCSS(cssURL) {
      return new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssURL;
        link.onload = () => {
          resolve();
          console.log(`CSS ${cssURL} has loaded!`);
        };
        document.head.appendChild(link);
      });
    },

    async loadTefOne(sp, tefOneEnv) {
      await this.loadCSS(`https://one.o9.de/${tefOneEnv}/${sp}/variables.css`);
      await this.loadCSS(`https://one.o9.de/${tefOneEnv}/${sp}/components.css`);
      await import(`https://one.o9.de/${tefOneEnv}/bundle.js`);
    },

    async init() {
      // sets props from URL and local storage
      const search = new URLSearchParams(window.location.search);
      propsQuery.forEach(({ name, type }) => {
        const value = search.get(name);
        if (value !== null) {
          this[name] = type === 'boolean' ? value === 'true' : value;
        }
      });
      propsLocalStorage.forEach(({ name }) => {
        const value = window.localStorage.getItem(name);
        if (value !== null) {
          this[name] = JSON.parse(value);
        }
      });
      // turn on debug mode
      window.localStorage.setItem('tef-coms-debug-mode', 'true');
      // listen to some events
      document.addEventListener('tef-coms', (e) => {
        const { detail } = e;
        if (detail.name === 'load/READY') {
          this.ready = true;
        }
        if (detail.name === 'load/ERROR') {
          this.setError(detail.error);
        }
      });
      await this.startServiceWorker();
      if (this.tefOne) {
        await this.loadTefOne(this.tefOne, this.tefOneEnv);
      }

      // create COMS micro-frontend
      createComs(this);
    },

    reload() {
      propsLocalStorage.forEach(({ name }) => {
        window.localStorage.setItem(name, JSON.stringify(this[name]));
      });
      const search = new URLSearchParams();
      propsQuery.forEach(({ name }) => {
        search.set(name, this[name]);
      });
      window.location = `${window.location.pathname}?${search}`; // reload page
    },

    save() {
      document.getElementById('consents').save()
        .then(() => this.setInfo('Saving consents successful.'))
        .catch((error) => this.setError(error));
    },

    showHtml() {
      const escapeHtml = (unsafe) => unsafe
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
      const html = document.getElementById('consents').outerHTML;
      this.setInfo(escapeHtml(html));
    },

    setScenario(value) {
      this.scenario = value;
    },

    isScenario(...value) {
      return value.includes(this.scenario);
    },

    setValue(prop, value) {
      this[prop] = value;
    },

    setInfo(text) {
      this.info = Array.isArray(text) ? text.join('<br />') : text;
      this.error = null;
    },

    setError(error) {
      this.error = error;
      if (error instanceof Error) {
        const { statusCode, statusText, stack } = error;
        const httpText = statusCode && statusText ? `<br />HTTP ${statusCode} ${statusText}` : '';
        const stackText = stack ? `<br />${stack}` : '';
        this.info = `${error.toString()}${httpText}${stackText}`;
      } else {
        this.info = error;
      }
    },
  },
});
